import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from '../i18n'
import { useAuth } from '../utils/auth'
import SEO from '../components/SEO'
import './AuthPages.css'

function LoginPage() {
  const { t } = useTranslation()
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Where to redirect after login (default: /dashboard)
  const from = (location.state as { from?: string })?.from || '/dashboard'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      navigate(from, { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('loginError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <SEO
        title="Log In"
        description="Log in to your Intervio account to access your dashboard and interview sessions."
        path="/login"
        noIndex
      />
      <div className="auth-card">
        <h1>{t('loginTitle')}</h1>
        <p className="auth-subtitle">{t('loginSubtitle')}</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="login-email">{t('authEmail')}</label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('authEmailPlaceholder')}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="login-password">{t('authPassword')}</label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('authPasswordPlaceholder')}
              required
              minLength={8}
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button
            type="submit"
            className="btn btn-primary btn-large auth-submit"
            disabled={loading}
          >
            {loading ? t('authLoggingIn') : t('loginButton')}
          </button>
        </form>

        <p className="auth-forgot-password">
          <Link to="/forgot-password">{t('loginForgotPassword')}</Link>
        </p>

        <p className="auth-switch">
          {t('loginNoAccount')}{' '}
          <Link to="/register">{t('loginSignUpLink')}</Link>
        </p>
      </div>
    </div>
  )
}

export default LoginPage
